'use client';

import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { classnames } from '@/utils/classnames/classnames';
import { PaginationProps } from './types';
import { usePageWindow } from './hooks/usePageWindow';

const Pagination = ({ currentPage, totalPages, onPageChange, siblingCount = 1, className }: PaginationProps) => {
  const items = usePageWindow(currentPage, totalPages, siblingCount);
  const isFirst = currentPage <= 1;
  const isLast = currentPage >= totalPages;

  if (totalPages <= 1) return null;

  const goTo = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    onPageChange?.(page);
  };

  const arrowClass = (disabled: boolean) =>
    classnames(
      'flex h-10 items-center gap-1 rounded px-3 text-14 font-medium',
      disabled ? 'cursor-not-allowed text-gray-400' : 'text-gray-800 hover:bg-neutral-100',
    );

  return (
    <nav aria-label="Pagination" className={classnames('flex items-center justify-center gap-1', className)}>
      <button
        type="button"
        onClick={() => goTo(currentPage - 1)}
        disabled={isFirst}
        aria-label="Previous page"
        className={arrowClass(isFirst)}
      >
        <ChevronLeftIcon className="size-4" aria-hidden="true" />
        <span className="hidden sm:inline">Prev</span>
      </button>

      <span className="text-14 px-2 text-gray-700 sm:hidden" aria-live="polite">
        Page {currentPage} of {totalPages}
      </span>

      <ul className="hidden items-center gap-1 sm:flex" role="list">
        {items.map((item) => {
          if (item.kind === 'ellipsis') {
            return (
              <li key={item.id} aria-hidden="true" className="text-14 px-2 text-gray-500">
                …
              </li>
            );
          }
          const isActive = item.page === currentPage;
          return (
            <li key={item.page}>
              <button
                type="button"
                onClick={() => goTo(item.page)}
                aria-current={isActive ? 'page' : undefined}
                aria-label={`Page ${item.page}`}
                className={classnames(
                  'text-14 flex h-10 min-w-10 items-center justify-center rounded px-3 font-medium',
                  isActive ? 'bg-gray-800 text-white' : 'text-gray-800 hover:bg-neutral-100',
                )}
              >
                {item.page}
              </button>
            </li>
          );
        })}
      </ul>

      <button
        type="button"
        onClick={() => goTo(currentPage + 1)}
        disabled={isLast}
        aria-label="Next page"
        className={arrowClass(isLast)}
      >
        <span className="hidden sm:inline">Next</span>
        <ChevronRightIcon className="size-4" aria-hidden="true" />
      </button>
    </nav>
  );
};

export default Pagination;
